// AICA Keyboard Shortcuts Help Overlay
// Full-screen overlay triggered by Cmd+/ showing all keyboard shortcuts
import { cn } from '@/lib/utils';
import { X } from 'lucide-react';
import { useEffect } from 'react';
import { useFocusTrap } from '@/hooks/useFocusTrap';
import { useTranslation } from 'react-i18next';
import { CONTROL_SQUARE } from '@/components/ui/control-size';

export interface KeyboardShortcutsHelpProps {
  isOpen: boolean;
  onClose: () => void;
}

interface ShortcutRow {
  descriptionKey: string;
  keys: string[];
}

interface ShortcutGroup {
  titleKey: string;
  shortcuts: ShortcutRow[];
}

function KbdBadge({ children }: { children: React.ReactNode }) {
  return (
    <kbd className="inline-flex items-center rounded border border-border bg-bg px-1.5 py-0.5 text-xs text-fg-subtle font-mono">
      {children}
    </kbd>
  );
}

function ChordBadge({ sequence }: { sequence: string[] }) {
  return (
    <span className="inline-flex items-center gap-1">
      {sequence.map((key, i) => (
        <span key={i} className="inline-flex items-center gap-1">
          <KbdBadge>{key}</KbdBadge>
          {i < sequence.length - 1 && (
            <span className="text-fg-subtle text-xs" aria-hidden="true">
              →
            </span>
          )}
        </span>
      ))}
    </span>
  );
}

export function KeyboardShortcutsHelp({ isOpen, onClose }: KeyboardShortcutsHelpProps) {
  const dialogRef = useFocusTrap<HTMLDivElement>(isOpen);
  const { t } = useTranslation();

  // Close on Escape
  useEffect(() => {
    if (!isOpen) return;

    const handleKeyDown = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        e.preventDefault();
        onClose();
      }
    };

    document.addEventListener('keydown', handleKeyDown);
    return () => document.removeEventListener('keydown', handleKeyDown);
  }, [isOpen, onClose]);

  if (!isOpen) return null;

  const isMac = typeof navigator !== 'undefined' && /Mac/.test(navigator.platform);
  const mod = isMac ? '⌘' : 'Ctrl';

  const groups: ShortcutGroup[] = [
    {
      titleKey: 'common:keyboardShortcuts.groups.navigation',
      shortcuts: [
        {
          descriptionKey: 'common:keyboardShortcuts.shortcuts.goDashboard',
          keys: [`${mod}1`],
        },
        {
          descriptionKey: 'common:keyboardShortcuts.shortcuts.goRecords',
          keys: [`${mod}2`],
        },
        {
          descriptionKey: 'common:keyboardShortcuts.shortcuts.goProjects',
          keys: [`${mod}3`],
        },
        {
          descriptionKey: 'common:keyboardShortcuts.shortcuts.goSettings',
          keys: [`${mod},`],
        },
      ],
    },
    {
      titleKey: 'common:keyboardShortcuts.groups.chords',
      shortcuts: [
        {
          descriptionKey: 'common:keyboardShortcuts.shortcuts.goDashboard',
          keys: ['g', 'd'],
        },
        {
          descriptionKey: 'common:keyboardShortcuts.shortcuts.goRecords',
          keys: ['g', 'r'],
        },
        {
          descriptionKey: 'common:keyboardShortcuts.shortcuts.goRecordsTable',
          keys: ['g', 't'],
        },
        {
          descriptionKey: 'common:keyboardShortcuts.shortcuts.goProjects',
          keys: ['g', 'p'],
        },
        {
          descriptionKey: 'common:keyboardShortcuts.shortcuts.goSettings',
          keys: ['g', 's'],
        },
        {
          descriptionKey: 'common:keyboardShortcuts.shortcuts.goAnalyses',
          keys: ['g', 'a'],
        },
        {
          descriptionKey: 'common:keyboardShortcuts.shortcuts.goAlerts',
          keys: ['g', 'l'],
        },
        {
          descriptionKey: 'common:keyboardShortcuts.shortcuts.goBilling',
          keys: ['g', 'b'],
        },
      ],
    },
    {
      titleKey: 'common:keyboardShortcuts.groups.actions',
      shortcuts: [
        {
          descriptionKey: 'common:keyboardShortcuts.shortcuts.commandPalette',
          keys: [`${mod}K`],
        },
        {
          descriptionKey: 'common:keyboardShortcuts.shortcuts.showHelp',
          keys: [`${mod}/`],
        },
        { descriptionKey: 'common:keyboardShortcuts.shortcuts.save', keys: [`${mod}S`] },
        {
          descriptionKey: 'common:keyboardShortcuts.shortcuts.upload',
          keys: [`${mod}U`],
        },
      ],
    },
    {
      titleKey: 'common:keyboardShortcuts.groups.general',
      shortcuts: [
        { descriptionKey: 'common:keyboardShortcuts.shortcuts.close', keys: ['Esc'] },
      ],
    },
  ];

  return (
    <div
      role="dialog"
      aria-modal="true"
      aria-labelledby="keyboard-shortcuts-title"
      className="fixed inset-0 z-modal flex items-center justify-center bg-bg/80 backdrop-blur-sm animate-fadeIn"
      onClick={onClose}
      onKeyDown={(e) => e.key === 'Escape' && onClose()}
      tabIndex={-1}
    >
      <div
        ref={dialogRef}
        className={cn(
          'w-full max-w-3xl rounded-lg border border-border bg-bg-elevated shadow-lg',
          'animate-scaleIn mx-4',
          'max-h-[80vh] overflow-y-auto'
        )}
        onClick={(e) => e.stopPropagation()}
        onKeyDown={(e) => {
          if (e.key === 'Escape') onClose();
          e.stopPropagation();
        }}
      >
        {/* Header */}
        <div className="flex items-center justify-between border-b border-border px-6 py-4">
          <h2 id="keyboard-shortcuts-title" className="text-base font-semibold text-fg">
            {t('common:keyboardShortcuts.title')}
          </h2>
          <button
            onClick={onClose}
            className={cn(
              CONTROL_SQUARE.md,
              'rounded-button p-2',
              'flex items-center justify-center',
              'text-fg-muted transition-all duration-150',
              'hover:bg-bg-subtle hover:text-fg',
              'focus:outline-none focus:ring-2 focus:ring-ring'
            )}
            aria-label={t('common:keyboardShortcuts.close')}
          >
            <X className="h-4 w-4" aria-hidden="true" />
          </button>
        </div>

        {/* Shortcut Groups Grid */}
        <div className="grid grid-cols-1 gap-6 p-6 sm:grid-cols-2">
          {groups.map((group) => (
            <div key={group.titleKey}>
              <h3 className="mb-3 text-xs font-medium uppercase tracking-wide text-fg-subtle">
                {t(group.titleKey)}
              </h3>
              <div className="space-y-2">
                {group.shortcuts.map((shortcut) => {
                  const firstKey = shortcut.keys[0] ?? '';
                  const isChord =
                    shortcut.keys.length > 1 &&
                    firstKey.length === 1 &&
                    !firstKey.includes('⌘') &&
                    !firstKey.toLowerCase().startsWith('ctrl');
                  return (
                    <div
                      key={`${group.titleKey}-${shortcut.descriptionKey}-${shortcut.keys.join('-')}`}
                      className="flex items-center justify-between gap-4"
                    >
                      <span className="text-sm text-fg-muted">
                        {t(shortcut.descriptionKey)}
                      </span>
                      <div className="shrink-0">
                        {isChord ? (
                          <ChordBadge sequence={shortcut.keys} />
                        ) : (
                          <KbdBadge>{shortcut.keys.join('')}</KbdBadge>
                        )}
                      </div>
                    </div>
                  );
                })}
              </div>
            </div>
          ))}
        </div>

        {/* Footer hint */}
        <div className="border-t border-border px-6 py-3 text-xs text-fg-subtle">
          <span>
            <kbd className="rounded-sm border border-border bg-bg px-1.5 py-0.5 font-mono">
              Esc
            </kbd>{' '}
            {t('common:commandPalette.footer.close')}
          </span>
        </div>
      </div>
    </div>
  );
}
